import React from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { formatTime, formatCurrency } from '../../lib/utils';
import { Slot } from '../../types';

interface SlotCardProps {
  slot: Slot;
  onSlotSelect: (slotConsultantMappingId: string) => void;
}

export const SlotCard: React.FC<SlotCardProps> = ({ slot, onSlotSelect }) => {
  const [selectedConsultant, setSelectedConsultant] = React.useState<string>('');
  
  const handleConsultantSelect = (consultantId: string) => {
    setSelectedConsultant(consultantId);
  };

  const handleBookSlot = () => {
    if (selectedConsultant) {
      const consultant = slot.consultants.find(c => c._id === selectedConsultant);
      if (consultant) {
        onSlotSelect(consultant.slotConsultantMappingId);
      }
    }
  };

  const handleDirectBook = () => {
    if (slot.consultants.length === 1) {
      onSlotSelect(slot.consultants[0].slotConsultantMappingId);
    }
  };

  return (
    <div className={`transition-all duration-200 rounded-lg border-2 p-4 shadow-sm hover:shadow-md ${
      slot.is_active 
        ? 'border-gray-200 hover:border-primary hover:shadow-lg' 
        : 'opacity-50 cursor-not-allowed border-gray-100'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-lg font-semibold text-gray-900">
          {formatTime(slot.time)}
        </div>
        <div className="text-lg font-bold text-primary">
          {formatCurrency(slot.pricing.price)}
        </div>
      </div>
      
      <div className="text-sm text-gray-600 mb-3">
        Duration: {slot.duration} minutes
      </div>

      {slot.consultants.length > 1 ? (
        <div className="space-y-3">
          <Select value={selectedConsultant} onValueChange={handleConsultantSelect}>
            <SelectTrigger className="w-full h-10 border-gray-300 rounded-lg">
              <SelectValue placeholder="Select consultant" />
            </SelectTrigger>
            <SelectContent>
              {slot.consultants.map((consultant) => (
                <SelectItem key={consultant._id} value={consultant._id}>
                  {consultant.first_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            onClick={handleBookSlot}
            disabled={!selectedConsultant || !slot.is_active}
            className="w-full"
            size="sm"
          >
            Book This Slot
          </Button>
        </div>
      ) : slot.consultants.length === 1 ? (
        <div className="space-y-3">
          <div className="text-sm text-gray-600">
            <span className="font-medium text-gray-900">Consultant:</span> {slot.consultants[0].first_name}
          </div>
          <Button 
            onClick={handleDirectBook}
            disabled={!slot.is_active}
            className="w-full"
            size="sm"
          >
            Book This Slot
          </Button>
        </div>
      ) : (
        <div className="text-sm text-gray-500 text-center py-3">
          No consultants available
        </div>
      )}
    </div>
  );
};
